import { api } from './api';

/**
 * Group sales (D-376): spaces and packages, leads, quotes, holds, bookings,
 * the board and the reports. Thin, like the other endpoint modules: the rules
 * are on the server.
 */

export type LeadStatus = 'NEW' | 'CONTACTED' | 'QUOTED' | 'HOLD' | 'BOOKED' | 'COMPLETED' | 'LOST' | 'CANCELLED';

export interface GroupProduct {
  id: string;
  code: string;
  name: string;
  kind: 'SPACE' | 'PACKAGE' | 'TICKET_BLOCK';
  venue_id: string | null;
  summary: string | null;
  description: string | null;
  capacity: number | null;
  min_guests: number | null;
  price_cents: number | null;
  per_game_limit: number | null;
  included_tickets: number;
  ticket_type_code: string | null;
  ticket_zone_code: string | null;
  whole_section_code: string | null;
  ticket_price_cents: number | null;
  map_section_code: string | null;
  public_listed: number;
  active: number;
  sort_order: number;
  inclusions: { id: string; quantity: number | null; label: string }[];
  options: { option_product_id: string; code: string; name: string; price_cents: number }[];
}

export interface GroupProductInput {
  code: string;
  name: string;
  kind: GroupProduct['kind'];
  venue_id: string | null;
  summary: string | null;
  description: string | null;
  capacity: number | null;
  min_guests: number | null;
  price_cents: number | null;
  per_game_limit: number | null;
  included_tickets: number;
  ticket_type_code: string | null;
  ticket_zone_code: string | null;
  whole_section_code: string | null;
  ticket_price_cents: number | null;
  map_section_code: string | null;
  public_listed: boolean;
  active: boolean;
  sort_order: number;
  inclusions: { quantity: number | null; label: string }[];
  options: { option_product_id: string; price_cents: number }[];
}

export interface Lookups {
  statuses: { code: LeadStatus; name: string; sort_order: number; is_open: number; requires_reason: number }[];
  group_types: { code: string; name: string }[];
  sources: { code: string; name: string }[];
  owners: { id: string; display_name: string }[];
  transitions: { from_status: LeadStatus; to_status: LeadStatus }[];
  today: string;
}

export interface Lead {
  id: string;
  reference: string;
  status: LeadStatus;
  source: string;
  group_type: string | null;
  contact_name: string;
  contact_email: string | null;
  contact_phone: string | null;
  organization_name: string | null;
  organization_id: string | null;
  contact_id: string | null;
  customer_id: string | null;
  headcount: number | null;
  flexible_dates: number;
  preferred_dates_text: string | null;
  interest_product_id: string | null;
  budget_text: string | null;
  message: string | null;
  owner_staff_account_id: string | null;
  next_follow_up_on: string | null;
  close_reason: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  last_activity_at: string;
}

export interface LeadListRow extends Lead {
  status_name: string;
  owner_name: string | null;
  interest_name: string | null;
  group_type_name: string | null;
  games: string | null;
  booked_cents: number;
}

export interface QuoteLine {
  line_no: number;
  kind: string;
  product_id: string | null;
  description: string;
  quantity: number;
  unit_price_cents: number;
  amount_cents: number;
}

export interface Quote {
  id: string;
  lead_id: string;
  version: number;
  status: 'DRAFT' | 'SENT' | 'ACCEPTED' | 'SUPERSEDED' | 'DECLINED';
  performance_id: string;
  product_id: string | null;
  headcount: number | null;
  extra_tickets: number;
  subtotal_cents: number;
  discount_cents: number;
  discount_reason: string | null;
  total_cents: number;
  deposit_cents: number;
  deposit_due_on: string | null;
  balance_due_on: string | null;
  valid_until: string | null;
  message: string | null;
  created_at: string;
  sent_at: string | null;
  viewed_at: string | null;
  lines: QuoteLine[];
  option_product_ids: string[];
  event_title: string;
  starts_at: string;
  timezone: string;
  product_name: string | null;
}

export interface Booking {
  id: string;
  lead_id: string | null;
  quote_id: string | null;
  product_id: string;
  event_id: string;
  performance_id: string;
  status: 'HELD' | 'BOOKED' | 'RELEASED' | 'CANCELLED';
  headcount: number | null;
  price_cents: number;
  extra_tickets: number;
  ticket_count: number;
  organization_name: string | null;
  organization_id: string | null;
  contact_name: string;
  inventory_hold_id: string | null;
  hold_expires_at: string | null;
  order_id: string | null;
  group_hold_id: string | null;
  booked_at: string | null;
  closed_at: string | null;
  close_reason: string | null;
  product_name: string;
  product_code: string;
  event_title: string;
  starts_at: string;
  timezone: string;
  lead_reference: string | null;
  order_number: string | null;
  total_cents: number | null;
  paid_cents: number | null;
  option_names: string | null;
}

export interface Activity {
  id: string;
  kind: string;
  body: string | null;
  from_status: string | null;
  to_status: string | null;
  data_json: string | null;
  actor_type: string;
  staff_name: string | null;
  created_at: string;
}

export interface LeadDetail {
  lead: Lead & { owner_name: string | null };
  activities: Activity[];
  quotes: Quote[];
  bookings: Booking[];
  games: { performance_id: string; event_title: string; starts_at: string; timezone: string; local_event_date: string }[];
  emails: { id: string; template_code: string; status: string; to_email: string; subject_snapshot: string | null; requested_at: string; sent_at: string | null; last_error: string | null }[];
  organization: { id: string; name: string; primary_email: string | null; phone: string | null; notes: string | null; house_account_enabled: number } | null;
}

export interface BoardCell {
  product_id: string;
  per_game_limit: number;
  held: number;
  booked: number;
  open: number;
  bookings?: { id: string; status: string; name: string; lead_id: string | null; reference: string | null }[];
}

export interface Board {
  products: { id: string; code: string; name: string; kind: string; per_game_limit: number; price_cents: number | null; capacity: number | null }[];
  games: { performance_id: string; event_id: string; event_title: string; starts_at: string; timezone: string; local_event_date: string; status: string; spaces: BoardCell[] }[];
}

export interface PlanInput {
  performance_id: string;
  product_id: string;
  option_product_ids: string[];
  extra_tickets: number;
  discount_cents: number;
  discount_reason?: string | null;
  headcount?: number | null;
}

export interface Priced {
  lines: { kind: string; description: string; quantity: number; amount_cents: number }[];
  subtotal_cents: number;
  discount_cents: number;
  total_cents: number;
  ticket_count: number;
  includes: string | null;
}

export interface Settings {
  inbox_email: string | null;
  default_owner_staff_account_id: string | null;
  hold_days: number;
}

const base = '/api/admin/group-sales';

export const groupBookingEndpoints = {
  lookups: () => api.get<Lookups>(`${base}/lookups`),
  settings: () => api.get<{ settings: Settings }>(`${base}/settings`),
  saveSettings: (settings: Settings) => api.put<{ settings: Settings }>(`${base}/settings`, settings),
  products: () => api.get<{ products: GroupProduct[] }>(`${base}/products`),
  product: (id: string) => api.get<{ product: GroupProduct; game_prices: { performance_id: string; price_cents: number; note: string | null }[] }>(`${base}/products/${id}`),
  createProduct: (input: GroupProductInput) => api.post<{ product: GroupProduct }>(`${base}/products`, input),
  updateProduct: (id: string, input: GroupProductInput) => api.put<{ product: GroupProduct }>(`${base}/products/${id}`, input),
  gamePrices: (id: string, prices: { performance_id: string; price_cents: number | null; note?: string | null }[]) => api.put<{ game_prices: unknown[] }>(`${base}/products/${id}/game-prices`, { prices }),
  board: (params: { from?: string; to?: string } = {}) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => Boolean(v)) as [string, string][]);
    return api.get<Board>(`${base}/board${query.size ? `?${query}` : ''}`);
  },
  price: (input: PlanInput) => api.post<Priced>(`${base}/price`, input),
  leads: (params: { status?: string; owner?: string; open?: boolean; due?: 'today'; q?: string; organization_id?: string } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.owner) query.set('owner', params.owner);
    if (params.open) query.set('open', '1');
    if (params.due) query.set('due', params.due);
    if (params.q) query.set('q', params.q);
    if (params.organization_id) query.set('organization_id', params.organization_id);
    return api.get<{ leads: LeadListRow[]; today: string }>(`${base}/leads${query.size ? `?${query}` : ''}`);
  },
  createLead: (input: Record<string, unknown>) => api.post<{ lead: Lead }>(`${base}/leads`, input),
  lead: (id: string) => api.get<LeadDetail>(`${base}/leads/${id}`),
  updateLead: (id: string, patch: Record<string, unknown>) => api.patch<{ lead: Lead }>(`${base}/leads/${id}`, patch),
  status: (id: string, status: LeadStatus, reason?: string | null) => api.post<{ lead: Lead }>(`${base}/leads/${id}/status`, { status, reason: reason ?? null }),
  note: (id: string, input: { kind: 'NOTE' | 'CALL'; body: string; next_follow_up_on?: string | null }) => api.post<{ ok: true }>(`${base}/leads/${id}/notes`, input),
  saveOrganization: (id: string) => api.post<{ lead: Lead }>(`${base}/leads/${id}/save-organization`),
  rebook: (id: string) => api.post<{ lead: Lead }>(`${base}/leads/${id}/rebook`),
  quote: (id: string, input: PlanInput & { deposit_cents: number; deposit_due_on: string | null; balance_due_on: string | null; valid_until: string | null; message: string | null; send: boolean }) =>
    api.post<{ quote: Quote; lines: QuoteLine[]; url: string }>(`${base}/leads/${id}/quotes`, input),
  sendQuote: (quoteId: string) => api.post<{ url: string }>(`${base}/quotes/${quoteId}/send`),
  quoteLink: (quoteId: string) => api.get<{ url: string }>(`${base}/quotes/${quoteId}/link`),
  hold: (id: string, input: { quote_id?: string | null; plan?: PlanInput | null; hold_until?: string | null; hold_tickets: boolean; note?: string | null }) => api.post<{ booking: Booking }>(`${base}/leads/${id}/hold`, input),
  book: (
    id: string,
    input: { booking_id?: string | null; quote_id?: string | null; plan?: PlanInput | null; payment: 'LINK' | 'INVOICE' | 'HOUSE_ACCOUNT'; balance_due_on: string; deposit_cents: number; deposit_due_on: string | null; send_link: boolean; auto_cancel: boolean; accept_current_price: boolean },
  ) => api.post<{ booking: Booking; order_id: string; order_number: string; total_cents: number; payment_request: { id: string; url: string } | null }>(`${base}/leads/${id}/book`, input),
  bookings: (params: { q?: string; organization_id?: string; live?: boolean } = {}) => {
    const query = new URLSearchParams();
    if (params.q) query.set('q', params.q);
    if (params.organization_id) query.set('organization_id', params.organization_id);
    if (params.live) query.set('live', '1');
    return api.get<{ bookings: Booking[] }>(`${base}/bookings${query.size ? `?${query}` : ''}`);
  },
  release: (bookingId: string, reason: string) => api.post<{ booking: Booking }>(`${base}/bookings/${bookingId}/release`, { reason }),
  cancel: (bookingId: string, reason: string) => api.post<{ booking: Booking }>(`${base}/bookings/${bookingId}/cancel`, { reason }),
  pipeline: (from: string, to: string) => api.get<PipelineReport>(`${base}/reports/pipeline?from=${from}&to=${to}`),
  revenue: (from: string, to: string) => api.get<RevenueReport>(`${base}/reports/revenue?from=${from}&to=${to}`),
  utilization: (from: string, to: string) => api.get<UtilizationReport>(`${base}/reports/utilization?from=${from}&to=${to}`),
};

export interface PipelineReport {
  totals: { leads: number; won: number; lost: number; open: number; conversion: number | null; value_cents: number };
  by_status: { status: string; name: string; leads: number; value_cents: number }[];
  by_owner: { owner_id: string | null; owner_name: string | null; leads: number; open: number; won: number; lost: number; value_cents: number }[];
  by_month: { month: string; leads: number; won: number; lost: number; value_cents: number }[];
  by_source: { source: string; name: string; leads: number; won: number }[];
}

export interface RevenueReport {
  totals: { bookings: number; tickets: number; total_cents: number; paid_cents: number };
  by_product: { product_id: string; product_name: string; bookings: number; tickets: number; total_cents: number; paid_cents: number }[];
  by_game: { performance_id: string; event_title: string; local_event_date: string; bookings: number; tickets: number; total_cents: number; paid_cents: number }[];
}

export interface UtilizationReport {
  games: number;
  spaces: { product_id: string; name: string; kind: string; games: number; capacity: number; booked: number; held: number; utilization: number | null }[];
  by_game: { performance_id: string; event_title: string; local_event_date: string; booked: number; held: number; open: number }[];
}

export const LEAD_TONE: Record<LeadStatus, 'ok' | 'warn' | 'bad' | 'neutral' | 'accent'> = {
  NEW: 'accent',
  CONTACTED: 'neutral',
  QUOTED: 'neutral',
  HOLD: 'warn',
  BOOKED: 'ok',
  COMPLETED: 'ok',
  LOST: 'bad',
  CANCELLED: 'bad',
};
